import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface ChoiceChipsBase<T extends string> {
  options: readonly ChoiceOption<T>[];
  "aria-label": string;
  className?: string;
  /** `pill` (rounded-pill h-8) or `badge` (compact, as in ai-models PII entity chips). */
  variant?: "pill" | "badge";
}

export interface SingleChoiceChipsProps<T extends string> extends ChoiceChipsBase<T> {
  multiple?: false;
  value: T | null;
  onValueChange: (value: T) => void;
}

export interface MultiChoiceChipsProps<T extends string> extends ChoiceChipsBase<T> {
  multiple: true;
  value: readonly T[];
  onValueChange: (value: T[]) => void;
}

export type ChoiceChipsProps<T extends string> = SingleChoiceChipsProps<T> | MultiChoiceChipsProps<T>;

const VARIANT_CLASS = {
  pill: "h-8 rounded-pill border border-line px-3 text-xs font-semibold text-fg-muted hover:border-primary hover:text-fg",
  badge: "badge badge-neutral h-7 border border-line",
} as const;

/**
 * `[data-choice-group(=multi)]` chips. Each chip is a toggle button with aria-pressed;
 * pressed chips use the primary soft tone plus a check icon (state never by colour alone).
 */
export function ChoiceChips<T extends string>(props: ChoiceChipsProps<T>) {
  const { options, className, variant = "pill" } = props;
  const isPressed = (value: T) => (props.multiple ? props.value.includes(value) : props.value === value);

  const toggle = (value: T) => {
    if (!props.multiple) {
      props.onValueChange(value);
      return;
    }
    const next = props.value.includes(value) ? props.value.filter((v) => v !== value) : [...props.value, value];
    props.onValueChange(next);
  };

  return (
    <div role="group" aria-label={props["aria-label"]} className={cn("flex flex-wrap gap-2", className)}>
      {options.map((option) => {
        const pressed = isPressed(option.value);
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            disabled={option.disabled}
            onClick={() => {
              toggle(option.value);
            }}
            className={cn(
              "inline-flex cursor-pointer items-center gap-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50 aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary-ink",
              VARIANT_CLASS[variant],
            )}
          >
            {pressed ? <Icon name="check" className="size-3" /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
